import type { GridApi, RangeSelectionChangedEvent } from "ag-grid-community";
import type { TData } from "@package/grid-utils";
import { useCallback, useRef } from "react";
import { atom, useAtomValue } from "jotai";

import { selectedCellsByRange, type TRangeCell } from "../../tableUtils";
import { useGridSettingsStatusMap } from "../../data";
import { isEodStorageType } from "./statuses/statusLogic";
import { gridApiAtomFamily } from "./stores";
import { marketGridId, usePaneId } from "../pane/context";
import { store } from "../modal";
import { funnel } from "remeda";

export type TRangeSelection = TRangeCell[][];
const rangeSelectionAtom = atom<TRangeSelection>([]);
rangeSelectionAtom.debugLabel = "rangeSelectionAtom";

export const getCurrentRangeSelection = () => store.get(rangeSelectionAtom);
export const useCurrentRangeSelection = () => useAtomValue(rangeSelectionAtom);

export function useRangeSelectionChanged() {
  const groupRangesRef = useRef<Set<number>>(new Set());
  const statusMap = useGridSettingsStatusMap();
  const paneId = usePaneId();

  return useCallback(
    (_: RangeSelectionChangedEvent) => {
      const api = store.get(gridApiAtomFamily(paneId));
      if (!api) return;

      const cellRanges = api.getCellRanges();
      const gridContainer = document.getElementById(marketGridId(paneId));

      if (gridContainer && cellRanges && cellRanges.length > 0) {
        const firstRange = cellRanges[0];
        const isSingleCell =
          firstRange.startRow?.rowIndex === firstRange.endRow?.rowIndex && firstRange.columns?.length === 1;

        if (!isSingleCell) {
          gridContainer.classList.add("hide-fill-handle");
        } else if (firstRange.columns?.[0]) {
          const column = firstRange.columns[0];
          const colDef = column.getColDef();
          const productId = colDef?.headerComponentParams?.productId;
          const status = productId ? statusMap?.[productId] || "listen" : "listen";

          if (status === "listen") {
            gridContainer.classList.add("hide-fill-handle");
          } else if (isEodStorageType(status)) {
            gridContainer.classList.add("hide-fill-handle");
          } else {
            gridContainer.classList.remove("hide-fill-handle");
          }
        } else {
          gridContainer.classList.remove("hide-fill-handle");
        }
      } else if (gridContainer) {
        gridContainer.classList.remove("hide-fill-handle");
      }

      const selectedRanges = selectedCellsByRange(
        api,
        [] as TRangeSelection,
        ({ column, entityId, rowNode, store, rangeIdx }) => {
          if (!column || !rowNode.id || rowNode?.rowIndex === undefined || rowNode?.rowIndex === null) return;

          store[rangeIdx] = store[rangeIdx] ?? [];
          store[rangeIdx].push({
            columnId: column.getId(),
            entityId,
            rowId: rowNode.id,
            rowIndex: rowNode.rowIndex,
            headerName: column?.getColDef()?.headerName || column.getId(),
            period: rowNode.data.period,
            cellValue: rowNode.data.cellValue,
          });
        },
      );

      const oldRowIdsToUpdate = store
        .get(rangeSelectionAtom)
        .flat()
        ?.map((x) => x.rowId);
      const newSelectedRange = selectedRanges[selectedRanges.length - 1]?.map((x) => x.rowId) || [];

      if (selectedRanges.length === 1) {
        const newIdsToUpdate = selectedRanges[0]?.map((x) => x.rowId);

        store.set(rangeSelectionAtom, selectedRanges);
        groupRangesRef.current = new Set();
        refreshTargetCells([...oldRowIdsToUpdate, ...newIdsToUpdate], api);
        return;
      }

      const groupedRanges: TRangeSelection = [];

      selectedRanges?.map((range, idx) => {
        const isPartOfCurrentGroup = groupRangesRef.current.has(idx);

        // we push an empty array to be populated with the current range
        // so length - 1 works as new group
        if (!isPartOfCurrentGroup) {
          groupedRanges.push([]);
        }

        // Add the current range to the last group in the groupedRanges
        groupedRanges[groupedRanges.length - 1].push(...range);
      });
      store.set(rangeSelectionAtom, groupedRanges);
      debouncedRefreshTargetCells.call({ api, rowIds: [...oldRowIdsToUpdate, ...newSelectedRange] });
    },
    [statusMap, paneId],
  );
}

function refreshTargetCells(rowIds: string[], api?: GridApi<TData>) {
  if (!api || api.isDestroyed()) {
    return;
  }
  const rowNodes = rowIds.map((id) => api.getRowNode(id)).filter(Boolean);
  api.refreshCells({ force: true, rowNodes });
}

const debouncedRefreshTargetCells = funnel(
  (arg: { api?: GridApi<TData>; rowIds: string[] }) => refreshTargetCells(arg.rowIds, arg.api),
  {
    reducer: (acc, curr) => ({ api: curr.api, rowIds: [...(acc?.rowIds || []), ...curr.rowIds] }),
    maxBurstDurationMs: 100,
  },
);
